import isNumber from 'lodash/isNumber';
import sortBy from 'lodash/sortBy';
import React, { Component } from 'react';
import Data from '../data';
import UI from '../ui';
import Utils from '../utils';
import ServiceHeader from './ServiceHeader.component';
import ServiceInfo from './ServiceInfo.component';

const { ServiceType } = Data;
const {
	WrappedAccordion,
	WrappedAccordionTabTemplate,
	WrappedButton,
	WrappedButtonType,
	WrappedTooltip,
} = UI;
const { withLayoutAndModalContexts, Logger } = Utils;

const FilterTile = {
	Cheapest: 'Cheapest',
	Recommended: 'Recommended',
	Fastest: 'Fastest',
};

class Services extends Component {
	constructor(props) {
		super(props);

		this.state = {
			numberOfServicesToShow: 6,
			showMoreExpanded: true,
			selectedTile: FilterTile.Recommended,
			activeIndex: -1,
		};
	}

	// return a sorted array based on selectedTile
	sortServicesBySelectedTile = (selectedTile, filteredServices) => {
		if (selectedTile === FilterTile.Cheapest) {
			// return a new array and sort by total amount in ascending order
			return sortBy(
				filteredServices,
				({ shipmentCost }) => shipmentCost?.totalAmount
			);
		} else if (selectedTile === FilterTile.Fastest) {
			// return a new array and sort by estimated delivery days, and then
			// total amount if estimated delivery days is equal, in ascending order
			return sortBy(filteredServices, [
				({ maxDeliverDays, minDeliverDays }) =>
					isNumber(maxDeliverDays) ? maxDeliverDays : minDeliverDays,
				service => service.shipmentCost?.totalAmount,
			]);
		}

		/**
		 * For Recommended, group services into two groups:
		 * group 2: parcel select (including GlobalPost) and media mail
		 * group 1: the rest
		 *
		 * Return a new array where group 2 is ranked beneath group 1, and
		 * services within their groups are sorted by its total amount.
		 */
		return sortBy(filteredServices, [
			({ serviceType }) =>
				serviceType === ServiceType.UspsMediaMail ||
				serviceType === ServiceType.UspsParcelSelect ||
				serviceType === ServiceType.GlobalPostParcelSelectSmartSaver
					? 2
					: 1,
			service => service.shipmentCost?.totalAmount,
		]);
	};

	onChangeSort = e => {
		const sortType = e.target.innerText;

		this.setState({
			selectedTile: sortType.includes(FilterTile.Recommended)
				? FilterTile.Recommended
				: sortType,
		});

		Logger.logToServer({
			category: 'ra_sort_change',
			value: sortType,
		});
	};

	// show more options handler
	showMoreHandler = () => {
		const { filteredServices } = this.props;

		this.setState({
			numberOfServicesToShow: filteredServices.length,
			showMoreExpanded: false,
		});

		Logger.logToServer({
			category: 'ui_interaction',
			ui_element: 'ra_scroll_down',
		});
	};

	onTabChange = activeIndex => {
		this.setState({
			activeIndex,
		});
	};

	render() {
		const { filteredServices, onServiceSelection } = this.props;
		const {
			showMoreExpanded,
			numberOfServicesToShow,
			selectedTile,
			activeIndex,
		} = this.state;
		const filterTiles = [
			FilterTile.Cheapest,
			FilterTile.Recommended,
			FilterTile.Fastest,
		];
		const sortedServices = this.sortServicesBySelectedTile(
			selectedTile,
			filteredServices
		);

		return (
			<div className="common-rate-advisor-services">
				<ul className="sort-tiles">
					{filterTiles.map(tile => (
						<li
							key={tile}
							name={tile}
							onClick={this.onChangeSort}
							className={selectedTile === tile ? 'sort-tile-active' : null}
							role="presentation"
							{...{ dataqa: `rate-advisor-services-sort-button-${tile}` }}
						>
							{tile}
							{tile === FilterTile.Recommended && (
								<>
									<div className="info-icon">
										<sup>&#9432;</sup>
									</div>
									<WrappedTooltip
										target="info-icon"
										position="bottom"
										className="common-rate-advisor-tootip"
									>
										<>
											<b>Recommended</b>
											<br />
											Sorts the available shipping options based on
											<br />
											the balance between cost and service level.
										</>
									</WrappedTooltip>
								</>
							)}
						</li>
					))}
				</ul>

				<WrappedAccordion onTabChange={this.onTabChange} dataqa="services">
					{sortedServices
						.slice(0, numberOfServicesToShow)
						.map((item, index) => (
							<WrappedAccordionTabTemplate key={item.key}>
								<ServiceHeader
									isActive={activeIndex === index}
									serviceData={item}
									onServiceSelection={onServiceSelection}
								/>
								<ServiceInfo serviceData={item} />
							</WrappedAccordionTabTemplate>
						))}
				</WrappedAccordion>

				{showMoreExpanded && sortedServices.length > numberOfServicesToShow && (
					<WrappedButton
						className="show-more-btn"
						label="Show more options"
						icon="pi pi-chevron-down"
						iconPos="right"
						buttonType={WrappedButtonType.TertiaryGrey}
						dataqa="services-cost-button"
						onClick={this.showMoreHandler}
					/>
				)}
			</div>
		);
	}
}

export default withLayoutAndModalContexts(Services);
